import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status, t }) => {
  const cls =
    status === "approved" ? "bg-green-500/20 text-green-400" :
    status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
    status === "rejected" ? "bg-red-500/20 text-red-400" :
    "bg-slate-700 text-slate-300";
  const label = status === "approved" ? t("منشور", "Live") :
    status === "pending" ? t("قيد المراجعة", "Pending") :
    status === "rejected" ? t("مرفوض", "Rejected") :
    t("مؤرشف", "Archived");
  return <Badge className={cls}>{label}</Badge>;
};

export default function MyProperties() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/properties/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm(t("هل أنت متأكد من حذف الإعلان؟", "Are you sure?"))) return;
    try {
      await axios.delete(`${API}/properties/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      load();
    } catch (e) { toast.error(t("فشل الحذف", "Delete failed")); }
  };

  return (
    <DashboardLayout title={t("إعلاناتي العقارية", "My Property Listings")}>
      <div className="space-y-4" data-testid="my-properties">
        <div className="flex justify-between items-center">
          <Link to="/housing" className="text-yellow-500 hover:underline">{t("← العودة للسوق", "← Back to marketplace")}</Link>
          <Button onClick={() => navigate("/housing/post")} className="btn-primary"><Plus className="ml-2" size={16} />{t("نشر جديد", "New listing")}</Button>
        </div>
        {loading ? (
          <div className="glass-card rounded-2xl h-40 skeleton" />
        ) : items.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center text-slate-400">
            {t("ليس لديك إعلانات بعد", "No listings yet")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((p) => (
              <div key={p.property_id} className="glass-card rounded-2xl overflow-hidden">
                <div onClick={() => navigate(`/housing/${p.property_id}`)} className="cursor-pointer">
                  <div className="h-40 overflow-hidden">
                    <ListingImage path={p.images?.[0]} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-white truncate">{p.title}</h3>
                      <StatusBadge status={p.status} t={t} />
                    </div>
                    <p className="text-sm text-slate-400">{p.city}, {p.state}</p>
                    {/* Owner pricing breakdown — only the owner sees these fields */}
                    {p.owner_price !== undefined ? (
                      <div className="mt-2 text-xs leading-tight space-y-0.5">
                        <p className="text-slate-300">{t("سعرك", "Your price")}: <span className="text-white font-bold">${Number(p.owner_price).toLocaleString()}</span></p>
                        <p className="text-slate-300">{t("سعر العميل", "Customer pays")}: <span className="text-yellow-400 font-bold">${Number(p.price).toLocaleString()}</span></p>
                        {p.partner_bonus !== undefined && (
                          <p className="text-emerald-400">{t("مكافأة الشريك بعد البيع", "Your partner bonus on sale")}: +${Number(p.partner_bonus).toLocaleString()}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-yellow-400 font-bold mt-1">${p.price?.toLocaleString()}</p>
                    )}
                  </div>
                </div>
                <div className="p-3 border-t border-slate-800 flex justify-end">
                  <button onClick={() => remove(p.property_id)} className="text-red-400 hover:text-red-300 flex items-center gap-1 text-sm" data-testid={`delete-prop-${p.property_id}`}>
                    <Trash2 size={14} /> {t("حذف", "Delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
